import cx from 'classnames';
import { useAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';
import { LinkProps } from 'next/link';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';

import useIsMobile from '../../../hooks/use-is-mobile';
import useLinkProps from '../../../hooks/use-link-props';
import { footerCurve } from '../../modules/footer/footer-upper-curve';
import Trustpilot from '../../modules/trustpilot';
import Chevrelon from '../../ui/icons/cheverlon';
import DividerTop from '../../ui/icons/dividers/top';
import BPImage from '../../ui/image';
import TechInfo from '../../ui/vehicle/vehicle-technical-information';

import IndentifyYourVehicle from './indentify-your-vehicle';
import VehicleSchema from './seo';
import SimilarVehicles from './similar-vehicles';
import StockCollapsible from './stock-collapsible';
import classes from './unique-vehicle.module.css';
import VehicleHeader from './vehicle-header';
import Button from '../../ui/button';
import IconClose from '../../ui/icons/close';
import ImageGallery from '../../ui/image-group/v2/image-gallery';
import { isSliderOpen, targetItem } from '../../ui/image-group/v2/state';
import Breadcrumbs from '../../modules/v2/breadcrumbs';
import VehicleDetails from './vehicle-details';
import PartInfo from '../product/part-info';
import ProductTitle from '../product/product-title';
import ProductPhotos from '../product/v2/photos';
import RelatedProducts from '../product/related-products';
import { getBrandHistory } from '../../../services/brand-history';
import { useRouter } from 'next/router';
import BrandHistory from '../../modules/v2/brand-history';
import SEOContent from '../product/seo-content-section';
import Reviews from '../../modules/reviews';

const UniqueVehicle: FunctionComponent<TPageVehicle> = ({
  groups,
  uniqueVehicle,
  similarVehicles,
}) => {
  const [, setVariant] = useAtom(footerCurve);
  const { t } = useTranslation();
  const { locale = 'en' } = useRouter();
  const getLink = useLinkProps();
  const isMobile = useIsMobile();
  const [showFullCarinfo, setShowFullCarinfo] = useState<boolean>(false);
  const [open, setOpen] = useAtom(isSliderOpen);
  const [index, setIndex] = useAtom(targetItem);
  const infoRef = useRef<HTMLDivElement>(null);

  const brandHistory = getBrandHistory({
    locale: locale,
    slug: uniqueVehicle?.car.brand_alias,
  });

  useEffect(() => {
    setOpen(false);
    setVariant(!!similarVehicles?.length ? 'PRIMARY' : 'SECONDARY');
  }, []);

  const list =
    uniqueVehicle?.images.filter(
      ({ image, image_big, image_thumb }) => image && image_big && image_thumb
    ) || [];

  const slides: { src: string; thumbnail: string; src_big: string }[] = [];
  list.forEach((element, index) => {
    slides.push({
      src: element.image,
      src_big: element.image_big,
      thumbnail: element.image_big,
    });
  });

  if (!groups || !uniqueVehicle) {
    return <div></div>;
  }

  const breadcrumbItems: Array<{
    href: LinkProps['href'];
    as: LinkProps['as'];
    text: string;
  }> = [];

  breadcrumbItems.push(
    ...[
      {
        ...getLink('brands'),
        text: t('common:brand.other'),
      },
      {
        ...getLink('models', {
          brand: uniqueVehicle.car.brand_slug,
        }),
        text: uniqueVehicle.car.brand_name,
      },
      {
        ...getLink('versions', {
          model: uniqueVehicle.car.model_slug,
        }),
        text: `${uniqueVehicle.car.model_name}`,
      },
    ]
  );

  if (uniqueVehicle.car.version_name) {
    breadcrumbItems.push(
      ...[
        {
          ...getLink('categories-with-vehicle', {
            category: uniqueVehicle.car.version_slug,
          }),
          text: `${uniqueVehicle.car.version_name}`,
        },
      ]
    );
  }

  return (
    <div>
      <VehicleSchema
        vehicle={uniqueVehicle.car}
        images={uniqueVehicle.images}
        breadcrumbs={breadcrumbItems}
      />
      <Breadcrumbs items={breadcrumbItems} currentPage={uniqueVehicle.sku} />

      <VehicleHeader
        id={uniqueVehicle.sku}
        uniqueVehicle={uniqueVehicle}
        backgroundImage={uniqueVehicle.banner_desktop}
        images={uniqueVehicle.images}
      />
      <section className='max-lg:hidden container mx-auto grid grid-cols-2 gap-6 pt-12 pb-4 items-center'>
        <div className="px-4 [&>div]:py-0">
          <TechInfo
            columns="2"
            technical_info={uniqueVehicle.technical_info || {}}
          />
        </div>
        <div className='relative'>
          <div className='max-lg:container max-lg:mx-auto lg:sticky lg:top-[calc(160px+24px)]'>
            <ProductPhotos qtt={1} list={uniqueVehicle.images} border isPP />
          </div>
        </div>
      </section>

      <div
        className={cx('lg:hidden relative shadow', 'z-10', 'bg-neutral-5')}
      >
        <button
          className={cx(
            'lg:hidden',
            'bg-neutral-5 text-blue-100',
            'font-bold',
            'px-4',
            'w-full',
            'flex',
            'items-center',
            'justify-between',
            'py-3'
          )}
          onClick={() => setShowFullCarinfo(!showFullCarinfo)}
        >
          {t('product:tec_info')}
          <Chevrelon className={cx('lg:hidden text-blue-100 h-4 w-4', ((showFullCarinfo && infoRef.current?.clientHeight) || !isMobile) && infoRef?.current?.clientHeight && infoRef?.current?.clientHeight > 0 ? 'rotate-180 transition-transform duration-300 ease-in-out' : 'rotate-0 transition-transform duration-300 ease-in-out')} />
        </button>

        <div
          className={cx(
            classes.collaspible,
            'relative',
            'bg-white',
            'text-neutral-80',
            'overflow-hidden'
          )}
          style={{
            height:
              (showFullCarinfo && infoRef.current?.clientHeight) || !isMobile
                ? `${infoRef.current?.clientHeight}px`
                : 0,
          }}
        >
          <div className={cx('relative')}>
            <div
              className={cx('bg-neutral-5 px-4', 'pt-0 pb-4', 'relative', 'z-10')}
              ref={infoRef}
            >
              <div className="[&>div]:py-0 text-neutral-80 bg-white">
                <TechInfo
                  showTitle={false}
                  columns="1"
                  technical_info={uniqueVehicle.technical_info || {}}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={cx('container', 'mx-auto', 'lg:py-12', 'grid', 'gap-12 max-lg:mt-8')}>
        <div>
          <div className={cx('px-4', 'text-center')}>
            <h2 className={cx('text-lg', 'font-bold')}>
              {t('unique-vehicle:stock_for_vehicle', { stock: uniqueVehicle.stock })}
            </h2>
            <span>{t('unique-vehicle:select_one_option')}</span>
          </div>
          <StockCollapsible
            groups={groups}
            products={uniqueVehicle.products}
            vehicle={{
              brand: uniqueVehicle.car.brand_name,
              model: uniqueVehicle.car.model_name,
              version: uniqueVehicle.car?.version_name || '',
            }}
          />
        </div>
      </div>
      <div className={cx('bg-brand-10', 'mt-8 lg:mt-0 pt-9')}>
        <div className={cx('text-center', 'pb-9')}>
          <IndentifyYourVehicle
            vehicle={{
              brand: uniqueVehicle.car.brand_name,
              model: uniqueVehicle.car.model_name,
              version: uniqueVehicle.car?.version_name || '',
              brand_alias: uniqueVehicle.car?.brand_alias,
            }}
          />
        </div>
      </div>
      <div className={cx('text-center', 'bg-white', 'hidden', 'lg:block')}>
        <Trustpilot className={'py-8 lg:py-6'} backgroundColor='bg-white' />
      </div>
      {!!similarVehicles?.length && (
        <div
          className={cx(
            'bg-neutral-10'
          )}
        >
          {/*<RelatedProducts relatedProducts={similarVehicles} bgColor='bg-white' />*/}
          <SimilarVehicles vehicles={similarVehicles} />
        </div>
      )}
      {
        brandHistory && (
          <section className="w-full bg-blue-10 py-12 lg:py-16 text-justify ">
            <BrandHistory
              {...brandHistory}
              brand={uniqueVehicle?.car.brand_name}
              vehicle={{
                brand: {
                  name: uniqueVehicle?.car.brand_name,
                  slug: uniqueVehicle?.car.brand_slug,
                  alias: uniqueVehicle?.car.brand_alias,
                },
                model: {
                  name: uniqueVehicle?.car.model_name,
                  slug: uniqueVehicle?.car.model_slug,
                },
                version: {
                  name: uniqueVehicle?.car.version_name,
                  slug: uniqueVehicle?.car.version_slug,
                },
              }}
              stock={
                (uniqueVehicle?.stock && uniqueVehicle?.stock.toLocaleString(locale) ||
                  '')
              }
            />
          </section>
        )
      }
      <Reviews />
      <Modal
        onAfterOpen={() => {
          document.body.style.overflow = 'hidden';
        }}
        onAfterClose={() => {
          document.body.style.overflow = 'auto';
          if (index > 2) {
            setIndex(0);
          }
        }}
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        overlayClassName={cx(
          'top-0',
          'bottom-0',
          'left-0',
          'right-0',
          'fixed',
          'z-50'
        )}
        className={cx(
          'top-0',
          'left-0',
          'right-0',
          'bottom-0',
          'bg-neutral-80',
          'fixed',
          'z-50'
        )}
        contentLabel="Images"
      >
        <div className="w-full bg-white pl-4 z-[1000] relative flex flex-row items-center justify-between">
          <div className="truncate">
            <>
              <b>
                <small>{uniqueVehicle.sku}</small>
              </b>{' '}
              |{' '}
              <small>{`${uniqueVehicle.car.brand_name} ${uniqueVehicle.car.model_name
                } ${uniqueVehicle.car.version_name || ''}`}</small>
            </>
          </div>
          <Button
            isFullWidth={false}
            onClick={() => setOpen(false)}
            className={cx('p-4', 'justify-self-end')}
          >
            <IconClose className={cx('h-4', 'text-neutral-80')} />
          </Button>
        </div>
        <div className="flex flex-col lg:flex-row h-[calc(100%-48px)] w-full lg:gap-4 justify-between">
          <ImageGallery list={slides} />
        </div>
      </Modal>
    </div>
  );
};

export default UniqueVehicle;
